import React, { useContext, useEffect, useState } from 'react'
import DataContext from '../data/context'
import { useHistory, useParams, useLocation, Redirect } from 'react-router-dom'
import { formatNumber, formatPrice, getDealerFromUrl } from '../utils'
import QRCode from 'qrcode.react'
import EmailConfirm from './EmailConfirm'
import { useMutation } from 'graphql-hooks'
import { LoadingIndicator } from './indicators'
import { useTranslation } from 'react-i18next'
import { useValuation } from '../hooks/useValuation'
import { fetchUserDataByCode } from '../data/session'
import analyticstracker from 'analyticstracker'
import { TAGMANAGER_PAGE_ARGS } from '../tagManager'

export const confirmEmailMutation = `mutation ConfirmEmail($data: ConfirmEmailAddressType!) { 
        confirmEmail(data: $data) {
            ok,            
        }
    }`

export const sendInspectionEmailMutation = `mutation SendInspectionEmail($data: SendInspectionMailType!) { 
        sendInspectionEmail(data: $data) {
            ok,            
        }
    }`

const Valuation = () => {
    const {
        inspection,
        inspectionStatus,
        fetchInspection,
        inspectionError,
        dealerKey,
        dealerConfig,
        dealer,
        setAuthenticated,
        setField,
    } = useContext(DataContext)
    const { valuation, loading: valuationLoading, hasValuation } = useValuation(inspection)
    const { key } = useParams()
    const location = useLocation()
    const [loading, setLoading] = useState(true)
    const [confirmMutation] = useMutation(confirmEmailMutation)
    const [sendInspectionMailMutation, { loading: resendPending }] = useMutation(sendInspectionEmailMutation)
    const [resendSuccess, setResendSuccess] = useState(false)
    const { t, i18n } = useTranslation()

    let history = useHistory()
    const showGuideValue = dealerConfig.valuationAverage

    const searchParams = new URLSearchParams(location.search)
    let confirmKey = undefined
    let userKey = undefined

    const shouldRenderMarketingSection = dealerKey === 'myway' || dealerKey === 'audi'

    if (searchParams.has('c')) {
        confirmKey = searchParams.get('c')
    }

    if (searchParams.has('u')) {
        userKey = searchParams.get('u')
    }

    useEffect(() => {
        let aTrack = analyticstracker()
        aTrack.trackImpression('page-impression')
    })

    const tagInfo = TAGMANAGER_PAGE_ARGS({
        section: 'valuation',
        language: i18n.language,
    })

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    useEffect(() => {
        if (inspection.key === undefined) {
            fetchInspection(key, getDealerFromUrl(window.location.pathname))
        }
    }, [key])

    useEffect(() => {
        if (confirmKey && userKey) {
            handleConfirmation(confirmKey, userKey)
        } else {
            setLoading(false)
        }
    }, [])

    const handleConfirmation = (confirmationKey, userKey) => {
        confirmMutation({
            variables: {
                data: {
                    inspectionKey: key,
                    confirmationKey: confirmationKey,
                    userKey: userKey,
                },
            },
        }).then((result) => {
            if (result.data && result.data.confirmEmail) {
                if (result.data.confirmEmail.ok) {
                    fetchUserDataByCode(dealerKey, userKey).then((user) => {
                        if (user) {
                            setField('userId', user.id)
                            setAuthenticated(user)
                            setLoading(false)
                        }
                    })

                    history.replace({
                        ...location,
                        search: '',
                    })
                }
            }
        })
    }

    const handleSendInspectionMail = () => {
        setResendSuccess(false)
        sendInspectionMailMutation({
            variables: {
                data: {
                    inspectionKey: inspection.key,
                },
            },
        }).then((result) => {
            if (result.data && result.data.sendInspectionEmail) {
                if (result.data.sendInspectionEmail.ok) {
                    setResendSuccess(true)
                }
            }
        })
    }

    if ((inspection.key === undefined && !inspectionError) || loading || (valuationLoading && inspection.userId)) {
        return (
            <div className="flex flex-1 items-center justify-center w-screen h-screen">
                <LoadingIndicator />
            </div>
        )
    }

    if (inspectionError && inspection.key === undefined) {
        return <Redirect to={`/${getDealerFromUrl(window.location.pathname)}/no-inspection-found`} />
    }

    if (inspectionStatus && (inspectionStatus === 'COMPLETED' || inspectionStatus === 'SYNCED')) {
        return <Redirect to={`/${getDealerFromUrl(window.location.pathname)}/inspection/${inspection.key}`} />
    }

    if (inspection.userId === undefined || inspection.userId === null) {
        return <EmailConfirm />
    }

    const QrCodeUrl = `${window.location.origin}/${getDealerFromUrl(window.location.pathname)}/inspection/${
        inspection.key
    }`

    const renderPurchaseProject = () => {
        const customText = dealerConfig.valuationPurchaseProject?.html

        if (!customText) {
            return
        }

        //If there's no purchaseproject don't show this
        //   if (inspection.purchase.onlyTakeOver) {
        //       return
        //   }
        const dealerName = inspection.dealerWebsite && inspection.dealerName ? inspection.dealerName : dealer.name
        const dealerWebsite = inspection.dealerWebsite || dealerConfig.redirectUrl

        const textToRender = customText
            .replaceAll('{{dealer_name}}', dealerName)
            .replaceAll('{{dealer_website}}', dealerWebsite)
            .replaceAll('{{dealer_main_name}}', dealer.name) // Can be extended with anything

        return (
            <>
                <div className="flex-1 px-8 lg:px-0 my-10 w-full">
                    <p
                        className="flex flex-col"
                        dangerouslySetInnerHTML={{
                            __html: textToRender,
                        }}
                    ></p>
                </div>
            </>
        )
    }

    const renderMarketingInfoSection = () => {
        const dealerKeys = {
            audi: '#BB0A30',
            myway: '#21b686',
        }
        const iconColor = dealerKeys?.[dealerKey] || dealerKeys['myway']
        return (
            <div className="mt-10">
                <div className="text-color_one text-24 font-bold mb-6 px-8 lg:px-0">
                    {t('marketing_heading', { dealer: dealer.name })}
                </div>
                <div
                    className="flex flex-col md:flex-row px-8 py-3 gap-3 w-full justify-between"
                    style={{ backgroundColor: '#F6F7F9' }}
                >
                    <div className="flex flex-col w-full md:w-1/3">
                        <div className="flex items-center mb-1">
                            <picture className="flex mr-2" style={{ height: '24px', width: '16px', fill: iconColor }}>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
                                    <path d="M320 480L64 480c-17.7 0-32-14.3-32-32L32 64c0-17.7 14.3-32 32-32l128 0 0 112c0 26.5 21.5 48 48 48l112 0 0 256c0 17.7-14.3 32-32 32zM240 160c-8.8 0-16-7.2-16-16l0-111.5c2.8 .7 5.4 2.1 7.4 4.2L347.3 152.6c2.1 2.1 3.5 4.6 4.2 7.4L240 160zM64 0C28.7 0 0 28.7 0 64L0 448c0 35.3 28.7 64 64 64l256 0c35.3 0 64-28.7 64-64l0-284.1c0-12.7-5.1-24.9-14.1-33.9L254.1 14.1c-9-9-21.2-14.1-33.9-14.1L64 0zm0 80c0 8.8 7.2 16 16 16l64 0c8.8 0 16-7.2 16-16s-7.2-16-16-16L80 64c-8.8 0-16 7.2-16 16zm0 64c0 8.8 7.2 16 16 16l64 0c8.8 0 16-7.2 16-16s-7.2-16-16-16l-64 0c-8.8 0-16 7.2-16 16zM224 432c0 8.8 7.2 16 16 16l64 0c8.8 0 16-7.2 16-16s-7.2-16-16-16l-64 0c-8.8 0-16 7.2-16 16zm64-96L96 336l0-64 192 0 0 64zM96 240c-17.7 0-32 14.3-32 32l0 64c0 17.7 14.3 32 32 32l192 0c17.7 0 32-14.3 32-32l0-64c0-17.7-14.3-32-32-32L96 240z" />
                                </svg>
                            </picture>
                            <span className="font-semibold text-color_one">{t('Simplified sales')}</span>
                        </div>
                        <div className="font-regular ml-6">
                            <div className="font-body">
                                <p>
                                    {t(
                                        'No administrative formalities when selling your car (technical inspection, invoicing, etc.).',
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col w-full md:w-1/3">
                        <div className="flex items-center mb-1">
                            <picture className="flex mr-2" style={{ height: '24px', width: '16px', fill: iconColor }}>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
                                    <path d="M166.5 7.2c15.7-9.5 35.3-9.5 51 0l16.4 10c2.8 1.7 6 2.6 9.3 2.5l19.2-.5c18.3-.4 35.4 9.4 44.2 25.5l9.2 16.8c1.6 2.9 3.9 5.2 6.8 6.8l16.8 9.2c16.1 8.8 25.9 25.8 25.5 44.2l-.5 19.2c-.1 3.3 .8 6.5 2.5 9.3l10 16.4c9.5 15.7 9.5 35.3 0 51l-10 16.4c-1.7 2.8-2.6 6-2.5 9.3l.5 19.2c.4 18.3-9.4 35.4-25.5 44.2l-16.8 9.2c-2.9 1.6-5.2 3.9-6.8 6.8l-9.2 16.8c-8.8 16.1-25.8 25.9-44.2 25.5l-19.2-.5c-3.3-.1-6.5 .8-9.3 2.5l-16.4 10c-15.7 9.5-35.3 9.5-51 0l-16.4-10c-2.8-1.7-6-2.6-9.3-2.5l-19.2 .5c-18.3 .4-35.4-9.4-44.2-25.5l-9.2-16.8c-1.6-2.9-3.9-5.2-6.8-6.8l-16.8-9.2c-16.1-8.8-25.9-25.8-25.5-44.2l.5-19.2c.1-3.3-.8-6.5-2.5-9.3l-10-16.4c-9.5-15.7-9.5-35.3 0-51l10-16.4 13.7 8.3-13.7-8.3c1.7-2.8 2.6-6 2.5-9.3l-.5-19.2c-.4-18.3 9.4-35.4 25.5-44.2l16.8-9.2c2.9-1.6 5.2-3.9 6.8-6.8l9.2-16.8c8.8-16.1 25.8-25.9 44.2-25.5l19.2 .5c3.3 .1 6.5-.8 9.3-2.5l16.4-10zm34.4 27.3c-5.4-3.3-12.3-3.3-17.7 0l-16.4 10c-8 4.9-17.3 7.4-26.7 7.1l-19.2-.5c-6.4-.2-12.3 3.3-15.3 8.9L96.4 76.9c-4.5 8.2-11.3 15-19.5 19.5L60 105.6c-5.6 3.1-9 9-8.9 15.3l.5 19.2c.2 9.4-2.3 18.6-7.1 26.7l-10 16.4c-3.3 5.4-3.3 12.3 0 17.7l10 16.4c4.9 8 7.4 17.3 7.1 26.7l-.5 19.2c-.2 6.4 3.3 12.3 8.9 15.3l16.8 9.2c8.2 4.5 15 11.3 19.5 19.5l9.2 16.8c3.1 5.6 9 9 15.3 8.9l19.2-.5c9.4-.2 18.6 2.3 26.7 7.1l16.4 10c5.4 3.3 12.3 3.3 17.7 0l16.4-10c8-4.9 17.3-7.4 26.7-7.1l19.2 .5c6.4 .2 12.3-3.3 15.3-8.9l9.2-16.8c4.5-8.2 11.3-15 19.5-19.5l16.8-9.2c5.6-3.1 9-9 8.9-15.3l-.5-19.2c-.2-9.4 2.3-18.6 7.1-26.7l10-16.4c3.3-5.4 3.3-12.3 0-17.7l-10-16.4c-4.9-8-7.4-17.3-7.1-26.7l.5-19.2c.2-6.4-3.3-12.3-8.9-15.3l-16.8-9.2c-8.2-4.5-15-11.3-19.5-19.5L278.4 60c-3.1-5.6-9-9-15.3-8.9l-19.2 .5c-9.4 .2-18.6-2.3-26.7-7.1l-16.4-10zM192 136a56 56 0 1 0 0 112 56 56 0 1 0 0-112zm88 56a88 88 0 1 1 -176 0 88 88 0 1 1 176 0zM1.3 441.8l41.2-97.9c1.5 1.8 3.6 4.8 6.9 10.8c4 7.4 9.1 14 15 19.6L41.8 428l38.1-5.7c6.2-.9 12.3 1.8 15.7 7.1L116.9 462l26-61.8c1.8 .9 4.1 2.2 6.9 3.9c7 4.3 14.5 7.4 22.2 9.3l-37.3 88.7c-2.3 5.5-7.4 9.2-13.3 9.7s-11.6-2.2-14.8-7.2L74.4 455.5l-56.1 8.3c-5.7 .8-11.4-1.5-15-6s-4.3-10.7-2.1-16zm248 60.4l-37.3-88.7c7.7-2 15.2-5.1 22.2-9.3c2.8-1.7 5.1-3 6.9-3.9l26 61.8 21.3-32.6c3.4-5.2 9.6-8 15.7-7.1l38.1 5.7-22.6-53.7c5.9-5.6 11-12.2 15-19.6c3.3-6 5.4-9 6.9-10.8l41.2 97.9c2.2 5.3 1.4 11.4-2.1 16s-9.3 6.9-15 6l-56.1-8.3-32.2 49.2c-3.2 5-8.9 7.7-14.8 7.2s-11-4.3-13.3-9.7z" />
                                </svg>
                            </picture>
                            <span className="font-semibold text-color_one">
                                {t('Assessment by a recognised expert')}
                            </span>
                        </div>
                        <div className="font-regular ml-6">
                            <div className="font-body">
                                <p>{t('book_an_appointment', { dealer: dealer.name })}</p>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col w-full md:w-1/3">
                        <div className="flex items-center mb-1">
                            <picture className="flex mr-2" style={{ height: '24px', width: '16px', fill: iconColor }}>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                                    <path d="M480 256A224 224 0 1 0 32 256a224 224 0 1 0 448 0zM0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256zm128 16l24.2 0c-.7-5-1-10.1-1-15.2c0-5.7 .4-11.3 1.2-16.8L128 240c-8.8 0-16-7.2-16-16s7.2-16 16-16l34.2 0c18.2-37.9 56.9-64 101.7-64l41.1 0c8.8 0 16 7.2 16 16s-7.2 16-16 16l-41.1 0c-26.3 0-49.6 12.6-64.4 32l88.5 0c8.8 0 16 7.2 16 16s-7.2 16-16 16l-103.1 0c-1.1 5.4-1.7 11-1.7 16.8c0 5.2 .5 10.3 1.4 15.2L288 272c8.8 0 16 7.2 16 16s-7.2 16-16 16l-89.6 0c14.7 20.3 38.5 33.5 65.5 33.5l41.1 0c8.8 0 16 7.2 16 16s-7.2 16-16 16l-41.1 0c-45.4 0-84.5-26.8-102.4-65.5L128 304c-8.8 0-16-7.2-16-16s7.2-16 16-16z" />
                                </svg>
                            </picture>
                            <span className="font-semibold text-color_one">{t('Secure payment guaranteed')}</span>
                        </div>
                        <div className="font-regular ml-6">
                            <div className="font-body">
                                <p>{t('After confirming the acquisition, we guarantee secure payment.')}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const renderPriceIndication = () => {
        const providerPriceIndications = {
            autralis: (
                <div className="flex flex-col items-center w-full">
                    <div
                        className="flex flex-col bg-color_one rounded-xl z-20 w-full"
                        style={{ height: 205, marginTop: -150, maxWidth: 260 }}
                    >
                        <React.Fragment>
                            <div className="w-full">
                                <div
                                    className={`flex text-white  items-center ${
                                        showGuideValue ? 'flex-col justify-center' : 'justify-between'
                                    } justify-center h-12 mt-8 font-bold`}
                                >
                                    {hasValuation && showGuideValue && (
                                        <React.Fragment>
                                            <span className="text-12">{t('Average')}</span>
                                            {valuation.guide && (
                                                <span className="text-30"> {formatPrice(valuation.guide)}</span>
                                            )}
                                        </React.Fragment>
                                    )}

                                    {hasValuation && !showGuideValue && (
                                        <div className="flex flex-1 flex-col items-center ">
                                            {valuation.min && (
                                                <React.Fragment>
                                                    <div>{t('From')}</div>
                                                    <div className="text-18">{formatPrice(valuation.min)}</div>
                                                </React.Fragment>
                                            )}
                                        </div>
                                    )}

                                    {hasValuation && !showGuideValue && (
                                        <div className="flex flex-1 flex-col items-center">
                                            {valuation.max && (
                                                <React.Fragment>
                                                    <div>{t('To')}</div>
                                                    <div className="text-18">{formatPrice(valuation.max)}</div>
                                                </React.Fragment>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-end justify-center">
                                <div
                                    className="flex absolute justify-between bottom-0 left-0 right-0 pl-5 pr-5 text-white font-bold"
                                    style={{ height: 70 }}
                                >
                                    {hasValuation && showGuideValue && (
                                        <div className="flex flex-col items-center ">
                                            {valuation.min && (
                                                <div className="text-18">{formatPrice(valuation.min)}</div>
                                            )}
                                        </div>
                                    )}

                                    {hasValuation && showGuideValue && (
                                        <div className="flex flex-col items-center">
                                            {valuation.max && (
                                                <div className="text-18">{formatPrice(valuation.max)}</div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <img
                                    src={require('../assets/price-indicator.svg')}
                                    style={{ width: 145, marginTop: 8 }}
                                    alt="price-indicator"
                                />
                            </div>
                        </React.Fragment>
                    </div>
                </div>
            ),
            indicata: (
                <div className="flex flex-col items-center w-full">
                    <div
                        className="flex flex-col bg-color_one rounded-xl z-20 w-full"
                        style={{ height: 205, marginTop: -150, maxWidth: 260 }}
                    >
                        <React.Fragment>
                            <div className="w-full">
                                <div className="flex flex-col text-white items-center justify-center h-12 mt-8 font-bold">
                                    {hasValuation ? (
                                        <>
                                            <div>
                                                {valuation.guide && (
                                                    <span className="text-30">
                                                        {t('valuation_price', { price: formatNumber(valuation.guide) })}
                                                    </span>
                                                )}
                                            </div>
                                            <div>
                                                <span className="text-12"> {t('Including VAT')}</span>
                                            </div>
                                        </>
                                    ) : (
                                        <div>
                                            <span className="text-30">?</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-end justify-center">
                                <div
                                    className="flex absolute justify-between bottom-0 left-0 right-0 pl-5 pr-5 text-white font-bold"
                                    style={{ height: 70 }}
                                ></div>
                                <img
                                    src={require('../assets/price-indicator.svg')}
                                    style={{ width: 145, marginTop: 8 }}
                                    alt="price-indicator"
                                />
                            </div>
                        </React.Fragment>
                    </div>
                </div>
            ),
        }

        return (
            <>
                <div className="text-color_one text-24 font-bold mb-8 px-8 lg:px-0">{t('Car price estimation')}</div>
                <div className="flex flex-col w-full md:flex-row">
                    <div className={`flex flex-col md:w-2/6 ${!dealerConfig.valuation ? 'hidden md:flex' : ''}`}>
                        <div className="flex flex-col items-center bg-color_four pl-8 pr-8 h-64">
                            <i className="fal fa-car text-70 text-color_three my-8" />
                        </div>
                        {dealerConfig.valuation && providerPriceIndications[dealerConfig?.provider]}
                    </div>
                    <div className="flex md:w-4/6 my-6 md:my-0">
                        {dealerConfig.valuation && hasValuation && (
                            <div className="leading-6 flex flex-col justify-around bg-white md:bg-color_four md:h-64">
                                <div
                                    className="space-y-3 px-8"
                                    dangerouslySetInnerHTML={{
                                        __html: t(
                                            'Based on your data, we were able to make a first takeover price estimation for your car. {{styling}} Attention! {{stylingEnd}} Depending on a number of unknown factors such as additional options, damage, ... this amount can vary and potentially even go outside of these limits.',
                                            {
                                                styling: '<span class="font-bold">',
                                                stylingEnd: '</span />',
                                                interpolation: { escapeValue: false },
                                            },
                                        ),
                                    }}
                                ></div>
                            </div>
                        )}
                        {dealerConfig.valuation && !hasValuation && (
                            <div className="leading-6 flex flex-col justify-around bg-white md:bg-color_four md:h-64">
                                <p
                                    className="leading-6 px-8"
                                    dangerouslySetInnerHTML={{
                                        __html: t(
                                            'Unfortunately we were not able to calculate a takeover price with this information. Please fill out the detailed inspection and we will get back to you with a correct price in 48hrs.',
                                            {
                                                styling: '<span class="font-bold">',
                                                stylingEnd: '</span />',
                                                interpolation: { escapeValue: false },
                                            },
                                        ),
                                    }}
                                ></p>
                            </div>
                        )}
                        {!dealerConfig.valuation && (
                            <p
                                className="leading-6"
                                dangerouslySetInnerHTML={{
                                    __html: t(
                                        'Through our simple mobile website you can use your smartphone to inspect your car yourself in {{styling}} only 5 steps! {{stylingEnd}} With the extra information, we’ll be able to estimate an accurate takeover price. It’ll only take up 15 minutes of your time and we’ll be here to guide you through the whole process.',
                                        {
                                            styling: '<span class="text-color_one font-bold">',
                                            stylingEnd: '</span />',
                                            interpolation: { escapeValue: false },
                                        },
                                    ),
                                }}
                            ></p>
                        )}
                    </div>
                </div>
            </>
        )
    }

    const renderExtendedInspectionInfo = () => {
        return (
            <>
                <div className="mt-10 mb-10 md:hidden px-8 lg:px-0">
                    {dealerConfig.valuation && (
                        <div className="text-color_one text-24 font-bold text-center">
                            {t('Want a more accurate price indication?')}
                        </div>
                    )}

                    <div className="text-color_one text-24 font-bold text-center mt-1 mb-8">
                        {t('Inspect your car yourself!')}
                    </div>

                    <div className="text-color_three text-center mb-8">
                        <p
                            className="leading-6"
                            dangerouslySetInnerHTML={{
                                __html: t(
                                    'Through our simple mobile website you can use your smartphone to inspect your car yourself in {{styling}} only 5 steps! {{stylingEnd}} It’ll only take up 15 minutes of your time and we will guide you through the whole process.',
                                    {
                                        styling: '<span class="text-color_one font-bold">',
                                        stylingEnd: '</span />',
                                        interpolation: { escapeValue: false },
                                    },
                                ),
                            }}
                        ></p>

                        <p className="mt-8 leading-6">
                            {t('With the extra information, we’ll be able to estimate an accurate takeover price.')}
                        </p>
                    </div>

                    <div
                        className="flex rounded-full uppercase bg-color_one text-white items-center justify-center text-center font-bold text-14 mb-4 cursor-pointer"
                        style={{ minHeight: 45 }}
                        onClick={() =>
                            history.push(`/${getDealerFromUrl(window.location.pathname)}/inspection/${inspection.key}`)
                        }
                    >
                        {t('Carry out the inspection yourself')}
                    </div>
                </div>

                <div className="md:bg-white md:rounded-lg hidden md:flex md:flex-col md:w-full mt-10 px-8 lg:px-0">
                    {/*{dealerConfig.valuation &&*/}
                    <div className="text-color_one text-24 font-bold mb-8">
                        {dealerConfig.valuation ? t('A more accurate price? ') : ''}{' '}
                        {t('Inspect your car yourself with your smartphone!')}
                    </div>

                    {/*<div className="text-color_one text-24 font-bold mb-8">{t('A more accurate price? Inspect your car yourself with your smartphone!')}</div>*/}

                    <div className="text-color_three mb-5">
                        {dealerConfig.valuation && (
                            <p
                                className="leading-6 mb-8"
                                dangerouslySetInnerHTML={{
                                    __html: t(
                                        'Through our simple mobile website you can use your smartphone to inspect your car yourself in {{styling}} only 5 steps! {{stylingEnd}} With the extra information, we’ll be able to estimate an accurate takeover price. It’ll only take up 15 minutes of your time and we’ll be here to guide you through the whole process.',
                                        {
                                            styling: '<span class="text-color_one font-bold">',
                                            stylingEnd: '</span />',
                                            interpolation: { escapeValue: false },
                                        },
                                    ),
                                }}
                            ></p>
                        )}

                        <div className="flex items-center justify-center">
                            <p className="leading-6">
                                {t('Grab your smartphone and scan the QR-code to start the detailed inspection. ')}
                            </p>

                            <div className="flex justify-center ml-8" style={{ maxWidth: 140, maxHeight: 140 }}>
                                <QRCode value={QrCodeUrl} />
                            </div>
                        </div>

                        <div className="mt-8 mb-10">
                            <div className="mb-1">{t('No QR-code scanner?')}</div>

                            <div className="flex">
                                {(resendSuccess || resendPending) && (
                                    <div className="mr-2">
                                        {resendSuccess ? (
                                            <i className="fa fa-check text-green-500" />
                                        ) : resendPending ? (
                                            <i className="fal fa-spinner" />
                                        ) : (
                                            ''
                                        )}
                                    </div>
                                )}
                                <div
                                    className="text-color_one underline cursor-pointer "
                                    onClick={handleSendInspectionMail}
                                >
                                    {' '}
                                    {t('Send me the link in an e-mail.')}
                                </div>
                            </div>
                        </div>

                        <hr className="solid border-color-five mb-10" />

                        <div className="text-black text-24 font-bold">{t('No smartphone?')}</div>

                        <p className="leading-6 mt-8">
                            {t(
                                'No problem! Do the inspection on your desktop. Just make sure you have a camera on hand to upload pictures of your car.',
                            )}
                        </p>
                    </div>

                    <div className="flex">
                        <div
                            className="btn-primary flex rounded-full uppercase bg-white border-2 border-black text-center items-center justify-center font-bold text-12 cursor-pointer tracking-widest px-6"
                            style={{ minHeight: 40 }}
                            onClick={() =>
                                history.push(
                                    `/${getDealerFromUrl(window.location.pathname)}/inspection/${inspection.key}`,
                                )
                            }
                        >
                            {t('Start inspection')}
                        </div>
                    </div>
                </div>
            </>
        )
    }

    return (
        <div className="flex flex-1 md:justify-center my-10">
            <div
                className="flex-col md:flex md:justify-center"
                style={{ maxWidth: 1024 }}
                data-tracking-event="page-impression"
                data-tracking-info={tagInfo}
            >
                {renderPriceIndication()}
                {shouldRenderMarketingSection ? renderMarketingInfoSection() : null}
                {dealerConfig.showInspection ? renderExtendedInspectionInfo() : renderPurchaseProject()}
            </div>
        </div>
    )
}
export default Valuation
